import React from 'react';
import { Typography, Card, CardContent, CircularProgress, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { FinancialSummaryCard } from '../step3/FinancialSummaryCard';
import { OrderInformationCard } from '../step3/OrderInformationCard';
import { PaymentInformationCard } from '../step3/PaymentInformationCard';
import { ProductsTableCard } from '../step3/ProductsTableCard';
import { PrintReceiptModal } from '../step3/PrintReceiptModal';
import CommercialDateTimePicker from '../shared/CommercialDateTimePicker';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { SALES_CUSTOMER_STEP_ENABLED } from '../../../config/feature-flags';
import SaleInvoiceToggleSection from '../SaleInvoiceToggleSection';
import { useOrdersPermissions } from '../../../hooks/useOrdersPermissions';
import { ConfirmSaleConfirmationModal } from '../../modals/confirmation';
import { useSalesStep3, SalesOrderCompletedPayload } from './SalesStep3.hooks';
import { formatCurrency } from '../../../utils/formatters/money';
import {
  MainContainer,
  LeftColumn,
  RightColumn,
  ActionsBox,
  BackButton,
  ConfirmButton,
  LossWarningList,
  InfoList,
} from './SalesStep3.styles';

export interface SalesStep3Props {
  onOrderCompleted?: (payload: SalesOrderCompletedPayload) => void;
}

const SalesStep3: React.FC<SalesStep3Props> = ({ onOrderCompleted }) => {
  const { setSaleDate, state: modalState, setHasInvoice } = useSalesModal();
  const { canModifySaleDate } = useOrdersPermissions();
  const {
    // State
    state,
    orderSummary,
    isProcessing,
    orderNumber,
    branchReceiptNumber,
    orderCompletedAt,
    showPrintModal,
    showConfirmModal,
    lossItems,
    paymentMethods,
    chargeCategories,
    isMixedSale,
    remoteGroupCount,
    canConfirmMixedSale,
    confirmError,
    sellingLocationId,
    // Handlers
    openConfirmModal,
    closeConfirmModal,
    handleConfirmSale,
    handleClosePrintModal,
    previousStep,
    setProductDelivered,
  } = useSalesStep3({ onOrderCompleted });

  return (
    <MainContainer>
      {/* Left Column - Pieces first (with delivery), then sale + payment info */}
      <LeftColumn>
        <ProductsTableCard
          selectedProducts={state.selectedProducts}
          sellingLocationId={sellingLocationId}
          onToggleDelivered={setProductDelivered}
        />

        {lossItems.length > 0 && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.warning.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '10px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'warning.light', mb: 1 }}>
                Piezas con pérdida
              </Typography>
              <LossWarningList>
                {lossItems.map((item) => (
                  <Typography
                    key={item.code}
                    component="li"
                    variant="body2"
                    sx={{ color: 'warning.light', lineHeight: 1.5 }}
                  >
                    {item.name}: pérdida de {formatCurrency(item.loss)}
                  </Typography>
                ))}
              </LossWarningList>
            </Box>
          </Box>
        )}

        <OrderInformationCard
          orderNumber={orderNumber}
          selectedLocation={state.selectedLocation}
          orderSummary={orderSummary}
          selectedCustomer={state.selectedCustomer}
          hasInvoice={state.hasInvoice}
          orderCharges={state.orderCharges}
          chargeCategories={chargeCategories}
        />

        {!SALES_CUSTOMER_STEP_ENABLED && (
          <SaleInvoiceToggleSection hasInvoice={state.hasInvoice} onChange={setHasInvoice} />
        )}

        <PaymentInformationCard
          paymentConfig={state.paymentConfig}
          orderSummary={orderSummary}
          paymentMethodOptions={paymentMethods}
        />
      </LeftColumn>

      {/* Right Column - Summary and Actions */}
      <RightColumn>
        <FinancialSummaryCard orderSummary={orderSummary} />

        {canModifySaleDate && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <CommercialDateTimePicker
              label="Fecha y hora de la venta"
              value={modalState.saleDate}
              onChange={setSaleDate}
            />
          </Box>
        )}

        {isMixedSale && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.warning.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.light' }}>
                Venta multi-sucursal
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Se crearán {remoteGroupCount} solicitud{remoteGroupCount === 1 ? '' : 'es'} de
                traslado automática{remoteGroupCount === 1 ? '' : 's'} para piezas de otras
                sucursales o del almacén. Las piezas locales se procesan de inmediato; las remotas
                quedan en logística hasta completar la venta.
              </Typography>
            </Box>
          </Box>
        )}

        {!canConfirmMixedSale && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'error.light', mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" color="error.light" sx={{ lineHeight: 1.5 }}>
              No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén.
            </Typography>
          </Box>
        )}

        {confirmError && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === 'dark' ? 0.18 : 0.08,
              ),
              border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
              borderRadius: '8px',
              padding: '12px 14px',
              mt: 2,
            })}
          >
            <InfoIcon sx={{ fontSize: 18, color: 'error.light', mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" color="error.light" sx={{ lineHeight: 1.5 }}>
              {confirmError}
            </Typography>
          </Box>
        )}

        <ActionsBox>
          <BackButton
            fullWidth
            variant="outlined"
            onClick={previousStep}
            disabled={isProcessing}
            startIcon={<ArrowBackIcon />}
          >
            {SALES_CUSTOMER_STEP_ENABLED ? 'Regresar al Paso 3' : 'Regresar al Paso 2'}
          </BackButton>

          <ConfirmButton
            fullWidth
            variant="contained"
            onClick={openConfirmModal}
            disabled={isProcessing || !canConfirmMixedSale}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {isProcessing ? 'Procesando...' : 'Confirmar Venta'}
          </ConfirmButton>
        </ActionsBox>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 2 }}>
              Al confirmar la venta:
            </Typography>
            <InfoList>
              <li>Se generará un número de venta único</li>
              {isMixedSale ? (
                <>
                  <li>
                    Se crearán {remoteGroupCount} solicitud{remoteGroupCount === 1 ? '' : 'es'} de
                    traslado (SOL) hacia {state.selectedLocation?.name || 'la sucursal de venta'}
                  </li>
                  <li>Las piezas de tu sucursal se actualizan de inmediato</li>
                  <li>
                    Las piezas remotas quedan en logística hasta recibir el traslado y completar la
                    venta
                  </li>
                </>
              ) : (
                <li>Se actualizará el inventario automáticamente</li>
              )}
              <li>
                {orderSummary.paymentStatus === 'complete'
                  ? isMixedSale
                    ? 'Las piezas locales pasarán a "Vendido"; las remotas al finalizar logística'
                    : 'Las piezas pasarán a estado "Vendido"'
                  : 'Se registrará el saldo pendiente'}
              </li>
              <li>Las piezas marcadas como entregadas quedarán registradas en la venta</li>
              <li>Podrás imprimir el comprobante de venta</li>
              <li>Se guardará el registro en el sistema</li>
            </InfoList>
          </CardContent>
        </Card>
      </RightColumn>

      <ConfirmSaleConfirmationModal
        open={showConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmSale}
        loading={isProcessing}
      />

      <PrintReceiptModal
        open={showPrintModal}
        onClose={handleClosePrintModal}
        orderNumber={orderNumber}
        branchReceiptNumber={branchReceiptNumber}
        orderCompletedAt={orderCompletedAt}
        selectedProducts={state.selectedProducts}
        selectedLocation={state.selectedLocation}
        orderSummary={orderSummary}
        orderCharges={state.orderCharges}
        chargeCategories={chargeCategories}
        paymentMethodOptions={paymentMethods}
        paymentConfig={state.paymentConfig}
      />
    </MainContainer>
  );
};

export default SalesStep3;
